'use client';

/**
 * Main hook for support chat functionality
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  SupportChatConfig,
  SupportChatUser,
  ChatMessage,
  SendMessageRequest,
  SendMessageResponse,
  SendMessageErrorResponse,
  ThreadId,
  ThreadInfo,
  SupportThread,
} from '../types/support-chat';
import { isSendMessageSuccess } from '../types/support-chat';
import { useThreadManagement } from './useThreadManagement';
import { useMessagePolling } from './useMessagePolling';
import { useThreadStorage } from './useThreadStorage';
import { collectBrowserContext } from '../lib/browser-context';

export interface CreateThreadOptions {
  userName?: string;
  userEmail?: string;
  initialMessage?: string;
  includeBrowserContext?: boolean;
}

export function useSupportChat(
  config: SupportChatConfig,
  user: SupportChatUser,
  isOpen: boolean,
  isUserActive: boolean = true,
  bearerToken?: string
) {
  const [threadId, setThreadId] = useState<ThreadId | null>(null);
  /**
   * Signed grant for the active thread. The server resolves the thread and the
   * sender from this instead of from client-supplied values — see
   * api/thread-grant.ts. It is issued once at thread creation and persisted
   * with the thread, so a thread stored before grants shipped has none and
   * cannot be continued.
   */
  const [threadGrant, setThreadGrant] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const attributeName = config.threadAttributeName || 'support_threads';
  const isAuthenticated = user.isAuthenticated !== false && user.accessToken !== undefined && user.cognitoSub !== undefined;
  
  const { threads, loadThreads, addThread, findThread, updateThreads } = useThreadStorage({
    accessToken: isAuthenticated ? (user.accessToken || null) : null,
    attributeName,
    cookieStorage: !isAuthenticated ? config.cookieStorage : undefined,
  });

  const { threadInfo, isLoading: isThreadLoading, error: threadError, createOrGetThread } = useThreadManagement(config, bearerToken);

  const { messages, isLoading: isMessagesLoading, error: messagesError, refreshMessages } = useMessagePolling(
    threadId,
    isOpen,
    config,
    isUserActive,
    bearerToken,
    threadGrant
  );

  // Load threads from storage on mount (Cognito for authenticated, cookies for unauthenticated)
  useEffect(() => {
    if (isAuthenticated && user.accessToken) {
      loadThreads();
    } else if (!isAuthenticated && config.cookieStorage) {
      loadThreads();
    }
  }, [isAuthenticated, user.accessToken, config.cookieStorage, loadThreads]);

  // Initialize thread when chat opens (only if no thread is selected)
  // This effect is intentionally simplified - actual thread creation with options
  // will be handled by the widget component when user info is collected
  useEffect(() => {
    if (isOpen && !threadId && !isInitializing) {
      // Allow unauthenticated users if cookie storage is configured
      if (!isAuthenticated && !config.cookieStorage) {
        setSendError('Cookie storage must be configured for unauthenticated users');
        return;
      }

      // Check if user has existing threads - if so, use the first one
      const existingThread = threads.length > 0 ? threads[0] : null;
      
      if (existingThread) {
        // Use existing thread (no browser context needed for existing threads).
        // A thread stored before grants shipped has no grant; it cannot be
        // continued, so surface that rather than silently polling a 403.
        if (!existingThread.grant) {
          setSendError('This conversation can no longer be opened. Please start a new one.');
          return;
        }
        setThreadId(existingThread.threadId);
        setThreadGrant(existingThread.grant);
      }
      // Note: New thread creation with browser context is handled by widget component
    }
  }, [isOpen, threadId, isInitializing, isAuthenticated, config.cookieStorage, threads]);

  // Update threadId when threadInfo changes (from createOrGetThread)
  useEffect(() => {
    if (threadInfo && threadInfo.threadId !== threadId) {
      setThreadId(threadInfo.threadId);
      if (threadInfo.grant) {
        setThreadGrant(threadInfo.grant);
      }

      // Store thread in storage (Cognito or cookies) if not already stored.
      // The grant MUST be persisted with it — it is issued once and every
      // later read/write needs it.
      const thread: SupportThread = {
        threadId: threadInfo.threadId,
        channelId: threadInfo.channelId,
        createdAt: threadInfo.createdAt,
        grant: threadInfo.grant,
      };
      findThread(threadInfo.threadId).then((existing) => {
        if (!existing) {
          addThread(thread).catch((err) => {
            console.error('Failed to store thread:', err);
          });
        }
      });
    }
  }, [threadInfo, threadId, addThread, findThread]);

  const sendMessage = useCallback(
    async (messageText: string, files?: File[]): Promise<boolean> => {
      if (!threadId || (!messageText.trim() && (!files || files.length === 0))) {
        return false;
      }

      // The grant is the credential the server requires; without it the post is
      // refused, so fail here with a message the visitor can act on.
      if (!threadGrant) {
        setSendError('This conversation can no longer be used. Please start a new one.');
        return false;
      }

      setSendError(null);

      try {
        const apiBaseUrl = config.apiBaseUrl || '/api/support';
        
        // Use FormData if we have files, otherwise use JSON for backward compatibility
        const hasFiles = files && files.length > 0;
        
        let response: Response;
        
        if (hasFiles) {
          // Create FormData for file uploads
          const formData = new FormData();
          formData.append('threadId', threadId);
          formData.append('message', messageText.trim());
          formData.append('userEmail', user.email);
          if (user.cognitoSub) {
            formData.append('cognitoSub', user.cognitoSub);
          }
          
          // Append all files
          files.forEach((file) => {
            formData.append('files', file);
          });

          const headers: Record<string, string> = {};
          if (bearerToken) {
            headers['Authorization'] = `Bearer ${bearerToken}`;
          }
          headers['x-support-thread-grant'] = threadGrant;

          response = await fetch(`${apiBaseUrl}/send-message`, {
            method: 'POST',
            headers,
            body: formData,
            // Don't set Content-Type header - browser will set it with boundary for FormData
          });
        } else {
          // Use JSON for text-only messages (backward compatibility)
          const request: SendMessageRequest = {
            // threadId/userEmail are ignored by the server (it uses the grant);
            // still sent so older deployments keep working during a rollout.
            threadId,
            message: messageText.trim(),
            userEmail: user.email,
            cognitoSub: user.cognitoSub, // Optional - can be undefined for unauthenticated users
            threadGrant,
          };

          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
          };
          if (bearerToken) {
            headers['Authorization'] = `Bearer ${bearerToken}`;
          }
          headers['x-support-thread-grant'] = threadGrant;

          response = await fetch(`${apiBaseUrl}/send-message`, {
            method: 'POST',
            headers,
            body: JSON.stringify(request),
          });
        }

        if (!response.ok) {
          const errorData: SendMessageErrorResponse = await response.json().catch(() => ({
            success: false,
            error: 'Failed to send message',
          }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const data: SendMessageResponse | SendMessageErrorResponse = await response.json();

        if (!isSendMessageSuccess(data)) {
          throw new Error(data.error || 'Failed to send message');
        }

        // Refresh messages to show the new message
        setTimeout(() => {
          refreshMessages();
        }, 500);

        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        setSendError(errorMessage);
        return false;
      }
    },
    [threadId, threadGrant, user, config, refreshMessages, bearerToken]
  );

  const switchThread = useCallback(
    (newThreadId: ThreadId) => {
      // Switching threads must swap the GRANT too. Carrying the previous
      // thread's grant over would either fail closed (the grant names the old
      // thread) or, without the server-side id check, be exactly the
      // cross-thread access this fix removes.
      const target = threads.find((t) => t.threadId === newThreadId);
      if (!target?.grant) {
        setSendError('This conversation can no longer be opened. Please start a new one.');
        return;
      }
      setThreadId(newThreadId);
      setThreadGrant(target.grant);
      setSendError(null);
    },
    [threads]
  );

  // Helper function to create a thread with formatted initial message
  const createThreadWithContext = useCallback(
    async (options?: CreateThreadOptions): Promise<ThreadInfo | null> => {
      const { userName, userEmail: overrideEmail, initialMessage: customMessage, includeBrowserContext = true } = options || {};

      // Use provided email or fall back to user email from hook
      const emailToUse = overrideEmail || user.email;

      // Collect browser context. It is passed STRUCTURED (not pre-formatted
      // text) so the server can render it as a compact Slack context block
      // below the message — never bulleted into the message body.
      const browserContext = includeBrowserContext ? collectBrowserContext() : undefined;

      // Build initial message — intro + the customer's message ONLY. No browser
      // context text here; that travels structured in `browserContext`.
      const messageParts: string[] = [];

      // Add user introduction if name is provided (anonymous users with form)
      if (userName) {
        messageParts.push(`Hello! My name is ${userName} and my email is ${emailToUse}.`);
      }

      // Add custom message if provided
      if (customMessage) {
        messageParts.push(customMessage);
      }

      // Default message if nothing provided
      if (messageParts.length === 0) {
        messageParts.push(`Support request from ${emailToUse}`);
      }

      const initialMessage = messageParts.join('\n');

      // Create thread with initial message + structured browser context
      const info = await createOrGetThread({
        userEmail: emailToUse,
        cognitoSub: user.cognitoSub,
        initialMessage,
        browserContext,
      });

      if (info) {
        setThreadId(info.threadId);
        if (info.grant) {
          setThreadGrant(info.grant);
        }

        // Store thread in storage (Cognito or cookies). The grant travels with
        // it — it is issued once and cannot be re-requested.
        const thread: SupportThread = {
          threadId: info.threadId,
          channelId: info.channelId,
          createdAt: info.createdAt,
          grant: info.grant,
        };
        addThread(thread).catch((err) => {
          console.error('Failed to store thread:', err);
        });
      }

      return info;
    },
    [user, createOrGetThread, addThread]
  );

  return {
    messages,
    threadId,
    threads,
    sendMessage,
    isLoading: isThreadLoading || isMessagesLoading || isInitializing,
    error: threadError || messagesError || sendError,
    refreshMessages,
    switchThread,
    createOrGetThread,
    createThreadWithContext,
    updateThreads,
    loadThreads,
  };
}

